/* "See It in Your Room": room photo intake, on-device crop and the AI preview dialog. */
(() => {
  'use strict';
  // The theme editor may run this file again when it re-renders the section. Every listener is
  // delegated on document and the card, dialog and settings are looked up when used, so one run
  // serves every render.
  if (window.__rossiRoomPreview || !document.getElementById('rossi-room-data')) return;
  window.__rossiRoomPreview = true;

  const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAGIC = [
    [0xFF, 0xD8, 0xFF],                                                      // JPEG
    [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],                        // PNG
    [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50], // WebP: "RIFF" <size> "WEBP"
  ];
  const MAX_BYTES = 15 * 1024 * 1024;
  const MAX_PIXELS = 50e6;
  const MIN_SIDE = 480;
  // The only sizes the Worker accepts; the photo is cover-cropped to the nearest one.
  const SIZES = [[1536, 1024], [1024, 1536], [1024, 1024]];
  const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
  const TOKEN_MS = 15000;
  const PASS_MS = 30000;   // a pass answer is reused this long: the endpoint allows 6 a minute per customer
  const CARD_SUB = 'Upload a photo. Preview this piece in your space.';
  const COPY = {
    small: 'This photo is too small. Please choose one at least 480 pixels on its shortest side.',
    heavy: 'This photo is over 15 MB. Please choose a smaller one or take a new photo.',
    unreadable: "We can't open this file. Please choose a JPG, PNG or WebP photo.",
    huge: 'That photo is too large for this device. Please choose a smaller one.',
    unavailable: "Room preview isn't available right now. Please try again later.",
    interrupted: 'Your connection was interrupted. Please try again.',
    noCheck: "We couldn't run the security check. Please disable content blockers for this page or try another browser.",
    signIn: 'Please sign in to use Room Preview.',
    unconfirmed: "We couldn't confirm your account. Please refresh the page, or sign out and back in.",
  };
  // Worker error codes; anything else (including a non-JSON body) reads as "unavailable".
  const ERRORS = {
    blocked: "Our AI checker couldn't use this photo. This often happens when people, pets or screens are in the shot. Please try another photo of the room.",
    // Also sent for the per-address and per-network limits; the copy stays the same for those.
    rate_limited: "You've used today's 3 previews. They reset overnight.",
    sign_in_required: COPY.signIn,
    busy: 'Lots of shoppers are creating previews right now. Please try again in a minute. If you have another preview running, wait for it to finish first.',
    timeout: 'This is taking longer than usual. Please try again.',
    bot_check_failed: "The security check didn't pass. Please try again.",
    bad_request: "We couldn't use that photo. Please try a different one.",
    too_large: "We couldn't use that photo. Please try a different one.",
  };
  const STAGES = [[0, 'Looking at your room…'], [6, 'Placing the {piece}…'], [14, 'Matching light and shadows…'], [24, 'Adding the finishing touches…'], [40, 'Almost there — thanks for waiting.']];
  const STEPS = { start: 1, down: 1, place: 2, generating: 3, result: 3 };
  // Same rules as the Worker (spec R1, R2, R33), so a shopper is never led through upload and
  // consent for a product image the Worker would refuse.
  const FILE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,180}\.(png|jpe?g|webp)$/i;
  const RESIZED = /(\.(png|jpe?g|webp|gif|progressive)|_(\d{1,5}x\d{0,5}|x\d{1,5}|pico|icon|thumb|small|compact|medium|large|grande|original|master)(_crop_[a-z]+)?)$/i;
  const UNIT = '(?:["”″]|\'\'|in\\.?|inches)?';
  const WDH = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${UNIT}\\s*W\\s*[x×]\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT}\\s*D\\s*[x×]\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT}\\s*H\\b`, 'i');
  const ARROWS = { ArrowLeft: [-5, 0], ArrowRight: [5, 0], ArrowUp: [0, -5], ArrowDown: [0, 5] };

  let root = null;        // the dialog in use, adopted on open
  let data = {};          // its JSON island
  let endpoint = '';
  let state = 'start';    // start | place | generating | result
  let health = '';        // '' | checking | ok | down
  let account = '';       // '' | in | out | unconfirmed | down: the customer pass check (only customers may make previews)
  let allowance = null;   // { limit, left }: the signed-in customer's previews today
  let accountAt = 0;      // when the last usable pass answer came; 0 once a preview may have changed the count
  let accountRun = 0;     // bumped per pass check, so only the newest answer is admitted
  let opener = null;
  let room = null, roomUrl = '', roomSize = SIZES[0];   // normalised JPEG: uploaded, and shown as "Original room"
  let result = null, resultUrl = '', resultVariant = '', remaining = null, showing = 'preview';
  let last = null, lastUrl = '';                        // photo remembered on this device (U7)
  let pin = null;         // [x, y] in whole percent of the photo
  let mode = 'add';
  let request = null;     // AbortController of the generation in flight
  let waiting = false;    // Generate was clicked and a Turnstile token is being minted
  let pick = 0;           // bumped per chosen file and per room change, so a slow decode cannot overwrite a newer photo
  let blocked = null;     // the photo the AI checker refused: retrying it costs previews and fails again
  let wake = null;        // screen wake lock (promise) while a request runs
  let closing = false;    // close() went back over the history entry open() added
  let ticker = 0;
  let botCheck = 'idle';  // Turnstile script: idle | loading | ready | failed
  let widget = null, tokenWait = null;

  const el = name => root.querySelector(`[data-rossi-room="${name}"]`);
  const all = selector => root.querySelectorAll(selector);
  const release = url => { if (url) URL.revokeObjectURL(url); };
  const isOpen = () => Boolean(root && !root.hidden);
  const isTouch = () => matchMedia('(pointer: coarse)').matches;
  const clamp = value => Math.min(100, Math.max(0, value));
  const announce = message => { if (root) el('status').textContent = message; };
  const setSrc = (image, url) => { if (!url) image.removeAttribute('src'); else if (image.getAttribute('src') !== url) image.src = url; };
  const previews = count => `${count} ${count === 1 ? 'preview' : 'previews'}`;
  // The limit a pass answer gave; C3's wording (3) before any answer.
  const usedUp = () => (allowance ? `You've used today's ${previews(allowance.limit)}. They reset overnight.` : ERRORS.rate_limited);

  /* ---------- Settings and product reference ---------- */

  function settings() {
    try { return JSON.parse(document.getElementById('rossi-room-data').textContent) || {}; } catch (_) { return {}; }
  }

  // https only (http just for local testing); anything else reads as "not configured" (spec R21).
  function serviceUrl(raw) {
    const value = String(raw || '').trim().replace(/\/+$/, '');
    try {
      const { protocol, hostname } = new URL(value);
      if (protocol === 'https:' || (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1'))) return value;
    } catch (_) { /* not a URL */ }
    return '';
  }

  // The Worker builds the CDN URL itself, so only "[products/]<file>[?v=<digits>]" is sent.
  function fileRef(url) {
    try {
      const { pathname, searchParams } = new URL(url, location.href);
      const [, dir, name] = pathname.match(/^\/(?:cdn\/shop|s\/files\/1\/0621\/1934\/6340)\/(files|products)\/([^/]+)$/) || [];
      if (!name || !FILE.test(name) || RESIZED.test(name.slice(0, name.lastIndexOf('.')))) return '';
      const version = searchParams.get('v') || '';
      return (dir === 'products' ? 'products/' : '') + name + (/^\d{1,12}$/.test(version) ? `?v=${version}` : '');
    } catch (_) { return ''; }
  }

  const productSection = () => document.querySelector('section[data-section-type="product"]');

  function variantId() {
    const select = productSection() && productSection().querySelector('select[name="id"]');
    return select ? select.value : '';
  }

  // This product page with the selected variant, so the QR handoff and a shared preview open the
  // same colour or finish. Only a numeric id is carried, never the rest of the query string.
  function productLink() {
    const variant = variantId();
    return `${location.origin}${location.pathname}${/^\d{1,20}$/.test(variant) ? `?variant=${variant}` : ''}`;
  }

  // What Liquid saw. The Worker never trusts it: the pass check below decides who may generate.
  const signedIn = () => settings().signedIn === true;

  // New customer accounts keep a relative return_to through the sign-in (on this store /account/login
  // drops return_url). Whether "#room-preview" survives it is unverified, so a one-shot flag backs it up.
  function signIn() {
    try { sessionStorage.setItem('rossiRoomResume', location.pathname); } catch (_) { /* storage blocked */ }
    location.href = `${settings().login}?return_to=${encodeURIComponent(`${productLink().slice(location.origin.length)}#room-preview`)}`;
  }

  // The selected variant's image when the Worker can use it, else the first usable product image.
  function reference(config) {
    const urls = [];
    try {
      const variants = JSON.parse(productSection().querySelector('script[data-rossi-variants]').textContent);
      urls.push(variants.find(variant => String(variant.id) === variantId()).media_image_url);
    } catch (_) { /* no variant data on this template */ }
    urls.push(...(Array.isArray(config.images) ? config.images : []));
    for (const url of urls) {
      const ref = fileRef(url);
      if (ref) return { ref, url };
    }
    return null;
  }

  function thumbUrl(url) {
    try {
      const thumb = new URL(url, location.href);
      thumb.searchParams.set('width', '120');
      return thumb.href;
    } catch (_) { return ''; }
  }

  // The page's own Add to Cart, found the way the sticky bar finds it (rossi-upgrade.js bindStickyBar).
  function mainCart() {
    const form = document.querySelector('section[data-section-type="product"] form[data-product-form]');
    const button = form && form.querySelector('[data-add-to-cart], button[name="add"]');
    return button && !button.disabled && button.getAttribute('aria-disabled') !== 'true' ? button : null;
  }

  function sizeLine() {
    const match = String(data.dimensions || data.description || '').match(WDH);
    const size = match ? `${+match[1]} W × ${+match[2]} D × ${+match[3]} H in` : String(data.dimensions || '').trim();
    return size ? `Size: ${size} — measure your space before ordering.` : '';
  }

  // Configured badly or no usable product image: the feature stays off on this page.
  function disable() {
    document.querySelectorAll('[data-rossi-room-open], #rossi-room-dialog').forEach(node => node.remove());
  }

  /* ---------- Painting ---------- */

  // Step 1 shows a notice in place of the upload controls when no preview can be made.
  function notice() {
    if (state !== 'start') return '';
    if (health === 'down' || account === 'down') return 'down';
    if (account === 'out') return 'signin';
    if (account === 'unconfirmed') return 'unconfirmed';
    return account === 'in' && allowance.left < 1 ? 'used' : '';
  }

  function paint() {
    if (!root) return;
    const gate = notice();
    const view = gate ? 'down' : state;
    const step = STEPS[state];
    const touch = isTouch();
    all('[data-rossi-room-view]').forEach(node => { node.hidden = node.dataset.rossiRoomView !== view; });
    all('[data-rossi-room-foot]').forEach(node => { node.hidden = node.dataset.rossiRoomFoot !== view; });
    all('[data-rossi-room-touch]').forEach(node => { node.hidden = !touch; });
    all('[data-rossi-room-desk]').forEach(node => { node.hidden = touch; });
    all('[data-rossi-room-step]').forEach(item => {
      const number = Number(item.dataset.rossiRoomStep);
      item.classList.toggle('is-done', number < step);
      item.classList.toggle('is-active', number === step);
      if (number === step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
    });
    const done = state === 'result';
    root.classList.toggle('is-result', done);   // the product row steps aside so a portrait result clears the footer
    el('title').textContent = done ? 'A new view of your room' : 'See it in your room';
    el('subtitle').textContent = done ? 'This piece, imagined in your space.' : 'A little inspiration, in your own space.';
    el('allowance').textContent = allowance ? `${previews(allowance.limit)} a day · ${allowance.left} left today` : '';
    el('allowance').hidden = view !== 'start' || account !== 'in';
    el('notice').textContent = gate === 'signin' ? COPY.signIn
      : gate === 'unconfirmed' ? COPY.unconfirmed
      : gate === 'used' ? usedUp()
      : COPY.unavailable;
    el('signin').hidden = gate !== 'signin';
    root.style.setProperty('--rossi-room-ratio', String(roomSize[0] / roomSize[1]));
    setSrc(el('photo'), roomUrl);
    setSrc(el('busy-photo'), roomUrl);
    all('[data-rossi-room-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.rossiRoomMode === mode)));
    el('spot-label').textContent = mode === 'replace' ? 'Tap the piece to replace (optional)' : 'Tap where it should go (optional)';
    paintPin();
    paintGenerate();
    if (done) paintResult();
    paintFooter();
    paintCard();
  }

  function paintPin() {
    el('pin').hidden = !pin;
    if (pin) Object.assign(el('pin').style, { left: `${pin[0]}%`, top: `${pin[1]}%` });
    el('unpin').hidden = !pin;
    el('spot-state').textContent = pin
      ? `Spot set ${pin[0]}% from the left and ${pin[1]}% from the top.`
      : 'No spot set; we will choose the most natural one.';
  }

  function paintGenerate() {
    const agreed = el('consent').checked;
    // After a moderation block the same photo stays refused; the alert offers "Choose another photo".
    const refused = Boolean(room) && room === blocked;
    el('generate').disabled = waiting || !agreed || refused;
    el('generate').textContent = waiting ? 'Checking…' : 'Generate Room Preview';
    el('need').hidden = agreed || refused;
    paintFooter();
  }

  function paintResult() {
    const original = showing === 'original';
    const image = el('result');
    setSrc(image, original ? roomUrl : resultUrl);
    image.alt = original ? 'Your original room photo' : `AI preview of ${data.title} in your room`;
    all('[data-rossi-room-show]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.rossiRoomShow === showing)));
    const size = sizeLine();
    el('size').textContent = size;
    el('size').hidden = !size;
    el('remaining').textContent = remaining === null ? '' : `${previews(remaining)} left today`;
    el('remaining').hidden = remaining === null;
    el('again').hidden = remaining === 0;   // it would lead to a Generate the Worker refuses
    el('hold').hidden = !isTouch();
    el('save').textContent = isTouch() ? 'Save or share' : 'Download';
    const cart = mainCart();
    el('cart').hidden = !cart;
    // Copied so "Pre-Order Now" and similar labels stay correct.
    if (cart) el('cart').textContent = (cart.querySelector('[data-add-to-cart-text]') || cart).textContent.replace(/\s+/g, ' ').trim() || 'Add to Cart';
  }

  function paintFooter() {
    const footer = el('footer');
    footer.hidden = el('alert').hidden && !root.querySelector('[data-rossi-room-foot]:not([hidden])');
    // Tab and scrollIntoView keep a focused control clear of the sticky footer (WCAG 2.4.11).
    el('panel').style.scrollPaddingBottom = footer.hidden ? '' : `${footer.offsetHeight + 12}px`;
  }

  // While a preview is being made, is ready or has failed and the dialog is closed, the card says
  // so (U4). Its subtitle is a polite live region, so it is left alone while the dialog covers it.
  function paintCard() {
    const card = document.querySelector('[data-rossi-room-open]');
    const sub = card && card.querySelector('[data-rossi-room-card-sub]');
    if (!sub || isOpen() || !signedIn()) return;   // signed out it keeps its sign-in line
    const text = state === 'generating' ? 'Creating your preview…'
      : state === 'result' && resultVariant === variantId() ? 'Your preview is ready — tap to view'
      : state === 'place' && root && !el('alert').hidden ? 'Your preview couldn’t be made — tap to see why'
      : CARD_SUB;
    if (sub.textContent !== text) sub.textContent = text;
    card.querySelector('.rossi-room-card__dot').hidden = text === CARD_SUB;
  }

  // code (a Worker error code) adds its way out: another photo after a block, or signing in.
  function showError(message, code = '') {
    el('error').textContent = message;
    el('alert').hidden = !message;
    el('retake').hidden = code !== 'blocked';
    el('relogin').hidden = code !== 'sign_in_required';
    paintFooter();
    paintCard();
    // The alert sits in the sticky footer, so it is on screen; focus reads it out (one channel:
    // the slot is not also a live region).
    if (message && isOpen()) el('error').focus({ preventScroll: true });
  }

  function setState(next) {
    state = next;
    showError('');
    if (state === 'start') refreshAccount();
    paint();
    if (isOpen()) enter();
  }

  // Runs whenever a step is shown in the open dialog (step change or reopen).
  function enter() {
    el('panel').scrollTop = 0;
    if (state === 'place' && botCheck === 'failed') showError(COPY.noCheck);
    // An error in the alert slot (e.g. a generation that failed while the dialog was closed) is
    // read out in place of the step, so it is heard once.
    if (!el('alert').hidden) {
      el('error').focus({ preventScroll: true });
    } else {
      el('title').focus({ preventScroll: true });
      announce(notice() ? el('notice').textContent
        : state === 'result' ? 'Your preview is ready.'
        : state === 'generating' ? 'Creating your preview.'
        : `Step ${STEPS[state]} of 3: ${['Upload', 'Place'][STEPS[state] - 1]}.`);
    }
    if (state === 'start') {
      offerLastPhoto();
      if (!isTouch()) drawHandoff();
    }
  }

  /* ---------- Service health (R23) ---------- */

  async function checkHealth() {
    if (health === 'ok' || health === 'checking') return;
    health = 'checking';
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 5000);
    let ok = false;
    try {
      const response = await fetch(`${endpoint}/v1/health`, { signal: timeout.signal });
      ok = response.ok && (await response.json()).ok === true;
    } catch (_) { /* offline, blocked, timed out or not JSON */ }
    clearTimeout(timer);
    health = ok ? 'ok' : 'down';
    if (!root) return;
    paint();
    // A failed pass check (same Worker) may have read it out already.
    if (!ok && isOpen() && state === 'start' && account !== 'down') {
      el('title').focus({ preventScroll: true });
      announce(COPY.unavailable);
    }
  }

  /* ---------- Customer pass ---------- */

  // Same origin, through Shopify's App Proxy, which tells the Worker who is signed in. Resolves
  // { signedIn, pass, limit, remaining }, or null without a usable answer: Shopify may swap an error
  // body for a storefront page, and its own 429s are not ours.
  async function askPass() {
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 5000);
    try {
      // The URL is the same for every customer; a random parameter keeps any cache from sharing a pass.
      const response = await fetch(`/apps/room-preview/pass?n=${Math.random().toString(36).slice(2)}`, { signal: timeout.signal });
      if (!response.ok || !(response.headers.get('Content-Type') || '').includes('application/json')) return null;
      const body = await response.json();
      if (body.signed_in === false) return { signedIn: false };
      const { pass, limit, remaining } = body;
      if (body.signed_in === true && typeof pass === 'string' && pass && Number.isInteger(limit) && Number.isInteger(remaining)) {
        return { signedIn: true, pass, limit, remaining };
      }
    } catch (_) { /* offline, timed out or not JSON */ } finally {
      clearTimeout(timer);
    }
    return null;
  }

  // Liquid saw a customer the proxy did not: Shopify sometimes sends an empty logged_in_customer_id
  // for a signed-in customer (an open bug with new customer accounts), so it is asked once more.
  async function fetchPass() {
    const customer = await askPass();
    if (!customer || customer.signedIn || !signedIn()) return customer;
    await new Promise(resolve => setTimeout(resolve, 1000));
    return askPass();
  }

  // The pass itself is never kept: only who is signed in and how many previews are left. Only shoppers
  // Liquid sees as signed in are asked, so signed_in:false here is that mismatch, not a sign-in to offer:
  // signing in again cannot fill the proxy's empty id.
  function admit(customer) {
    account = !customer ? 'down' : customer.signedIn ? 'in' : 'unconfirmed';
    accountAt = customer ? Date.now() : 0;
    if (account === 'in') allowance = { limit: customer.limit, left: customer.remaining };
  }

  // On the way into step 1, the only step that shows who may generate.
  function refreshAccount() {
    if (!signedIn()) account = 'out';
    else if (Date.now() - accountAt > PASS_MS) checkAccount();
  }

  // In parallel with the health check. Until the answer comes no earlier gate is shown or read out;
  // then a notice it brings is read out like that check's, otherwise the allowance is.
  async function checkAccount() {
    const run = ++accountRun;
    account = '';
    const customer = await fetchPass();
    if (run !== accountRun) return;   // a newer check (a reopen, or Generate) has the say
    admit(customer);
    paint();
    if (!isOpen() || state !== 'start' || health === 'down') return;   // "unavailable" was read out already
    const gate = notice();
    const message = el(gate ? 'notice' : 'allowance').textContent;
    if (!message || el('status').textContent === message) return;
    if (gate) el('title').focus({ preventScroll: true });
    announce(message);
  }

  /* ---------- Turnstile (R24) ---------- */

  // Loaded on first open, not with the page. A failed load is retried on the next open or Generate.
  function loadBotCheck() {
    if (botCheck === 'loading' || botCheck === 'ready') return;
    if (window.turnstile) { botCheck = 'ready'; return; }
    botCheck = 'loading';
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => {
      botCheck = window.turnstile ? 'ready' : 'failed';
      if (botCheck === 'ready') runCheck(); else settle('');
    };
    script.onerror = () => {
      script.remove();
      botCheck = 'failed';
      if (tokenWait) settle('');
      else if (isOpen() && state === 'place') showError(COPY.noCheck);
    };
    document.head.append(script);
  }

  function settle(token) {
    if (!tokenWait) return;
    const { resolve, timer } = tokenWait;
    tokenWait = null;
    clearTimeout(timer);
    resolve(token);
  }

  // A fresh single-use token minted at click time. Resolves '' when the check cannot run.
  function freshToken() {
    settle('');
    return new Promise(resolve => {
      tokenWait = { resolve, timer: setTimeout(() => settle(''), TOKEN_MS) };
      loadBotCheck();
      if (botCheck === 'ready') runCheck();
    });
  }

  function runCheck() {
    if (!tokenWait) return;
    const box = el('turnstile');
    try {
      if (widget) {
        window.turnstile.reset(widget);
      } else {
        widget = window.turnstile.render(box, {
          sitekey: data.sitekey,
          action: 'room_preview',
          appearance: 'interaction-only',
          execution: 'execute',
          size: 'flexible',
          theme: 'light',
          'response-field': false,
          callback: settle,
          'error-callback': () => { settle(''); return true; },
          'timeout-callback': () => settle(''),
          'unsupported-callback': () => settle(''),
          // The 15 s cap pauses while the shopper solves a visible challenge and restarts after it (E7).
          // Turnstile's own timeout-callback still ends a challenge nobody solves.
          'before-interactive-callback': () => {
            box.classList.add('is-interactive');
            if (tokenWait) clearTimeout(tokenWait.timer);
            if (!isOpen()) return;
            // Focus was on Generate, now disabled. From the check's box one Tab enters the widget,
            // whose iframe sits in a closed shadow root the focus trap cannot see (R24).
            const active = document.activeElement;
            if (!active || active === document.body || active === el('generate')) box.focus({ preventScroll: true });
            announce('Please complete the security check below.');
          },
          'after-interactive-callback': () => {
            box.classList.remove('is-interactive');
            if (tokenWait) tokenWait.timer = setTimeout(() => settle(''), TOKEN_MS);
          },
        }) || null;
        if (!widget) return settle('');
      }
      window.turnstile.execute(widget);
    } catch (_) { settle(''); }
  }

  /* ---------- Photo ---------- */

  async function sniff(file) {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    return MAGIC.some(signature => signature.every((byte, i) => byte === null || head[i] === byte));
  }

  // A decode that silently failed (seen on memory-starved phones) leaves a blank canvas.
  function uniform(canvas) {
    const probe = document.createElement('canvas');
    probe.width = probe.height = 8;
    const context = probe.getContext('2d');
    context.drawImage(canvas, 0, 0, 8, 8);
    const pixels = new Uint32Array(context.getImageData(0, 0, 8, 8).data.buffer);
    return pixels.every(value => value === pixels[0]);
  }

  // Decodes with EXIF orientation applied, cover-crops to the nearest allowed aspect and
  // re-encodes as JPEG at the exact pixel size. Re-encoding on the device also drops EXIF/GPS.
  async function normalise(file) {
    let source, url = '';
    try {
      source = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (_) {
      url = URL.createObjectURL(file);
      source = new Image();
      source.src = url;
    }
    try {
      if (url) await source.decode();
      const width = source.naturalWidth || source.width, height = source.naturalHeight || source.height;
      if (!(Math.min(width, height) >= MIN_SIDE)) return { message: COPY.small };
      if (width * height > MAX_PIXELS) return { message: COPY.huge };
      const off = size => Math.abs(Math.log(width / height * size[1] / size[0]));
      const size = SIZES.reduce((best, next) => (off(next) < off(best) ? next : best));
      const [w, h] = size;
      const scale = Math.max(w / width, h / height);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff';   // transparent PNG/WebP areas would otherwise export black
      context.fillRect(0, 0, w, h);
      context.imageSmoothingQuality = 'high';
      context.drawImage(source, (width - w / scale) / 2, (height - h / scale) / 2, w / scale, h / scale, 0, 0, w, h);
      if (uniform(canvas)) return { message: COPY.unreadable };
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      return blob ? { blob, size } : { message: COPY.unreadable };
    } finally {
      release(url);
      if (source.close) source.close();
    }
  }

  async function useFile(file) {
    if (!file) return;
    const run = ++pick;
    showError('');
    el('drop').setAttribute('aria-busy', 'true');   // a large photo takes a moment to decode
    let outcome = { message: COPY.unreadable };
    if (file.size > MAX_BYTES) {
      outcome = { message: COPY.heavy };
    } else if (TYPES.includes(file.type)) {
      try { if (await sniff(file)) outcome = await normalise(file); } catch (_) { /* unreadable */ }
    }
    if (run !== pick) return;
    el('drop').removeAttribute('aria-busy');
    if (outcome.blob) setRoom(outcome.blob, outcome.size); else showError(outcome.message);
  }

  // Sets (or with null clears) the room photo. Anything tied to the previous photo goes with it,
  // including a decode still running for a photo chosen earlier.
  function setRoom(blob, size) {
    pick += 1;
    el('drop').removeAttribute('aria-busy');
    release(roomUrl);
    release(resultUrl);
    release(lastUrl);
    room = blob;
    roomUrl = blob ? URL.createObjectURL(blob) : '';
    roomSize = size || SIZES[0];
    result = null;
    resultUrl = lastUrl = '';
    pin = null;
    all('.rossi-room__file').forEach(input => { input.value = ''; });
    el('consent').checked = false;   // permission was given for the previous photo
    setState(blob ? 'place' : 'start');
  }

  /* ---------- Remembered photo (U7) ---------- */

  // IndexedDB "rossi-room" / "photos" / "last". Resolves null on any failure (private mode, blocked
  // storage): the feature then simply works without reuse. With existing = true it only looks and
  // never creates the database.
  function photos(access, run, existing = false) {
    return new Promise(resolve => {
      try {
        const opening = indexedDB.open('rossi-room', 1);
        opening.onupgradeneeded = () => (existing ? opening.transaction.abort() : opening.result.createObjectStore('photos'));
        opening.onerror = () => resolve(null);
        opening.onsuccess = () => {
          const db = opening.result;
          try {
            const tx = db.transaction('photos', access);
            const action = run(tx.objectStore('photos'));
            tx.oncomplete = () => { db.close(); resolve(action.result); };
            tx.onerror = tx.onabort = () => { db.close(); resolve(null); };
          } catch (_) { db.close(); resolve(null); }
        };
      } catch (_) { resolve(null); }
    });
  }

  // Only the normalised room photo is kept: never a result, note or token.
  const remember = () => photos('readwrite', store => store.put({ blob: room, size: roomSize, time: Date.now() }, 'last'));
  const forget = () => photos('readwrite', store => store.delete('last'));
  const usable = saved => Boolean(saved && saved.blob instanceof Blob && Date.now() - saved.time < KEEP_MS
    && SIZES.some(size => String(size) === String(saved.size)));

  async function offerLastPhoto() {
    const saved = await photos('readonly', store => store.get('last'));
    const fresh = usable(saved);
    if (saved && !fresh) forget();
    if (!isOpen() || state !== 'start') return;
    release(lastUrl);
    last = fresh ? saved : null;
    lastUrl = last ? URL.createObjectURL(last.blob) : '';
    setSrc(el('last-thumb'), lastUrl);
    el('last').hidden = !last;
  }

  function forgetLastPhoto() {
    forget();
    release(lastUrl);
    last = null;
    lastUrl = '';
    el('last').hidden = true;
    el('title').focus({ preventScroll: true });
    announce('Your last room photo was removed from this device.');
  }

  /* ---------- Desktop handoff QR (U2) ---------- */

  // Drawn on a canvas from the lazily loaded qrcode-generator (assets/rossi-room-qr.js);
  // if that cannot load, the link is shown as text instead.
  // Redrawn when the variant changed since the last draw.
  function drawHandoff() {
    const box = el('handoff');
    const link = `${productLink()}#room-preview`;
    if (box.dataset.link === link) return;
    box.dataset.link = link;
    const fallback = () => {
      box.dataset.state = 'text';
      el('qr').hidden = true;
      el('qr-link').textContent = box.dataset.link.replace(/^https?:\/\//, '');
      el('qr-link').hidden = false;
    };
    const draw = () => {
      try {
        const code = window.qrcode(0, 'M');
        code.addData(box.dataset.link);
        code.make();
        const count = code.getModuleCount(), cell = 4, quiet = 4;   // 4-module quiet zone per the QR spec
        const canvas = el('qr');
        canvas.width = canvas.height = (count + quiet * 2) * cell;
        const context = canvas.getContext('2d');
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#302f2a';
        for (let row = 0; row < count; row += 1) {
          for (let col = 0; col < count; col += 1) {
            if (code.isDark(row, col)) context.fillRect((col + quiet) * cell, (row + quiet) * cell, cell, cell);
          }
        }
        canvas.hidden = false;
        box.dataset.state = 'drawn';
      } catch (_) { fallback(); }
    };
    if (typeof window.qrcode === 'function') return draw();
    if (box.dataset.state === 'text') return fallback();   // the library failed to load earlier
    if (box.dataset.state === 'loading') return;            // onload draws the latest link
    box.dataset.state = 'loading';
    const script = document.createElement('script');
    script.src = data.qr;
    script.async = true;
    script.onload = () => (typeof window.qrcode === 'function' ? draw() : fallback());
    script.onerror = () => { script.remove(); fallback(); };
    document.head.append(script);
  }

  /* ---------- Generate ---------- */

  function buildForm(token, ref, pass) {
    const body = new FormData();
    body.append('room', room, 'room.jpg');
    body.append('product_image', ref);
    body.append('mode', mode);
    const hint = el('hint').value.trim();
    if (hint) body.append('hint', hint);
    if (pin) {
      body.append('place_x', String(pin[0]));
      body.append('place_y', String(pin[1]));
    }
    body.append('consent', '1');
    body.append('turnstile', token);
    body.append('pass', pass);
    return body;
  }

  // One POST. No custom headers: it must stay a CORS "simple request" (no preflight).
  async function send(body, signal) {
    let response;
    try {
      response = await fetch(`${endpoint}/v1/preview`, { method: 'POST', body, signal });
    } catch (_) {
      return { message: COPY.interrupted };
    }
    if (response.ok && (response.headers.get('Content-Type') || '').startsWith('image/jpeg')) {
      const count = response.headers.get('X-Previews-Remaining') || '';
      try {
        return { blob: await response.blob(), remaining: /^\d{1,6}$/.test(count) ? Number(count) : null };
      } catch (_) {
        return { message: COPY.interrupted };
      }
    }
    let code = '';
    try { code = String((await response.json()).error || ''); } catch (_) { /* HTML or empty body */ }
    // ES2017 on purpose (no Object.hasOwn): older iOS in-app browsers would throw here.
    return { code, message: Object.prototype.hasOwnProperty.call(ERRORS, code) ? ERRORS[code] : COPY.unavailable };
  }

  async function generate() {
    if (state !== 'place' || !room || waiting || !el('consent').checked || room === blocked) return;
    // The variant is read together with its product image, before the token wait (R28).
    const variant = variantId();
    const product = reference(data);
    if (!product) return showError(COPY.unavailable);
    const photo = room;
    showError('');
    waiting = true;
    paintGenerate();
    announce('Running a quick security check.');
    // A fresh pass for every Generate, fetched while the token is minted.
    const run = ++accountRun;
    let [token, customer] = await Promise.all([freshToken(), fetchPass()]);
    if (run === accountRun) admit(customer);
    waiting = false;
    paintGenerate();
    // Moved on, or closed the dialog, while the check ran: nothing is counted yet, so the attempt is dropped.
    if (state !== 'place' || room !== photo || !isOpen()) return;
    if (!customer) return showError(COPY.unavailable);
    if (!customer.signedIn) return showError(COPY.unconfirmed);
    // The Worker's own count: a preview it would refuse is not sent.
    if (customer.remaining < 1) return showError(usedUp());
    if (!token) return showError(COPY.noCheck);

    accountAt = 0;   // a block, a cancel or a failure may change the count; step 1 asks again
    const current = request = new AbortController();
    setState('generating');
    startProgress();
    holdScreen();
    let outcome = await send(buildForm(token, product.ref, customer.pass), current.signal);
    if (outcome.code === 'bot_check_failed' && request === current) {
      token = await freshToken();   // one automatic retry with a fresh token
      if (request === current) outcome = token ? await send(buildForm(token, product.ref, customer.pass), current.signal) : { message: COPY.noCheck };
    }
    if (request !== current) return;   // cancelled: only the request in flight may change the dialog
    request = null;
    freeScreen();
    stopProgress();
    if (!outcome.blob) {
      if (outcome.code === 'blocked') blocked = photo;
      // Not setState(): the error is read out once, without a step announcement before it.
      state = 'place';
      paint();
      return showError(outcome.code === 'rate_limited' ? usedUp() : outcome.message, outcome.code);
    }
    release(resultUrl);
    result = outcome.blob;
    resultUrl = URL.createObjectURL(result);
    resultVariant = variant;
    remaining = outcome.remaining;
    if (allowance && remaining !== null) {
      allowance.left = remaining;
      accountAt = Date.now();   // the header is the count after this preview
    }
    showing = 'preview';
    remember();
    // R28: a preview belongs to its variant. If the shopper switched meanwhile, Add to Cart would add
    // a piece they never saw in their room, so it is step 2 with the photo kept instead.
    setState(variant === variantId() ? 'result' : 'place');
  }

  // Only "Cancel preview" aborts. Closing the dialog does not: the call is already counted and paid for.
  function cancel() {
    if (!request) return;
    request.abort();
    request = null;
    settle('');
    freeScreen();
    stopProgress();
    setState('place');
  }

  // Best effort for the life of the request (R25). The browser drops the lock whenever the page is
  // hidden, so it is asked for again when the shopper comes back (see visibilitychange below).
  function holdScreen() {
    if (navigator.wakeLock) wake = navigator.wakeLock.request('screen').catch(() => null);
  }

  function freeScreen() {
    if (wake) wake.then(lock => lock && lock.release()).catch(() => {});
    wake = null;
  }

  function startProgress() {
    stopProgress();   // never two tickers
    const started = Date.now();
    const piece = /^[a-z]{3,20}$/i.test(data.type || '') ? data.type.toLowerCase() : 'piece';
    let shown = '';
    const tick = () => {
      const seconds = (Date.now() - started) / 1000;
      // Quick at first, then slower: about 66% at 20 s and 88% at 40 s; never full until the image arrives.
      el('bar').style.transform = `scaleX(${((1 - Math.exp(-seconds / 18)) * 0.97).toFixed(3)})`;
      const stage = STAGES.filter(([at]) => seconds >= at).pop()[1].replace('{piece}', piece);
      if (stage === shown) return;
      if (shown) announce(stage);
      shown = stage;
      el('stage').textContent = stage;
    };
    tick();
    ticker = setInterval(tick, 500);
  }

  function stopProgress() {
    clearInterval(ticker);
    ticker = 0;
  }

  /* ---------- Result ---------- */

  function save(share) {
    const name = `rossi-room-preview-${data.handle}.jpg`;
    // Share sheet on phones (from the click, so it counts as a user gesture); download elsewhere and
    // from the "Download" text action phones also get (E5).
    if (share && navigator.canShare && navigator.share) {
      const files = [new File([result], name, { type: 'image/jpeg' })];
      // Named, labelled as AI and linked, so whoever receives it knows which piece it is.
      const named = { files, title: data.title, text: `AI preview of ${data.title} in my room: ${productLink()}` };
      const share = navigator.canShare(named) ? named : navigator.canShare({ files }) ? { files } : null;
      if (share) {
        navigator.share(share).catch(() => { /* dismissed */ });
        return;
      }
    }
    const link = document.createElement('a');
    link.href = resultUrl;
    link.download = name;
    document.body.append(link);
    link.click();
    link.remove();
  }

  function addToCart() {
    const cart = mainCart();
    if (!cart) return paintResult();
    cart.click();
    close();   // so the theme's cart drawer is what the shopper sees next
  }

  function openViewer() {
    const image = el('viewer-img');
    setSrc(image, el('result').getAttribute('src'));
    image.alt = el('result').alt;
    el('panel').inert = true;
    el('viewer').hidden = false;
    zoom(false);
    el('viewer-close').focus({ preventScroll: true });
  }

  function closeViewer() {
    if (el('viewer').hidden) return;
    el('viewer').hidden = true;
    el('panel').inert = false;
    if (isOpen()) el('enlarge').focus({ preventScroll: true });
  }

  // 2x with native scrolling for panning; keeps the tapped point (or the centre) in view.
  function zoom(on, event) {
    const viewer = el('viewer'), image = el('viewer-img'), stage = el('viewer-stage');
    const zoomIn = on === undefined ? !viewer.classList.contains('is-zoomed') : on;
    viewer.classList.remove('is-zoomed');
    image.style.width = '';
    if (zoomIn) {
      const box = image.getBoundingClientRect();
      const x = event && event.detail ? (event.clientX - box.left) / box.width : 0.5;
      const y = event && event.detail ? (event.clientY - box.top) / box.height : 0.5;
      viewer.classList.add('is-zoomed');
      image.style.width = `${box.width * 2}px`;
      stage.scrollLeft = x * box.width * 2 - stage.clientWidth / 2;
      stage.scrollTop = y * box.height * 2 - stage.clientHeight / 2;
    }
    el('viewer-zoom').setAttribute('aria-pressed', String(zoomIn));
  }

  /* ---------- Dialog ---------- */

  function open(trigger) {
    const dialog = document.getElementById('rossi-room-dialog');
    if (!dialog || isOpen()) return;
    data = settings();
    endpoint = serviceUrl(data.endpoint);
    const product = endpoint && reference(data);
    if (!product) return disable();
    if (dialog !== root) {
      // First open, or the theme editor rendered a fresh dialog: retire the one moved to <body> earlier.
      document.querySelectorAll('body > .rossi-room').forEach(stale => { if (stale !== dialog) stale.remove(); });
      if (widget && window.turnstile) {
        try { window.turnstile.remove(widget); } catch (_) { /* already gone */ }
      }
      widget = null;
      root = dialog;
      // The footer also grows when the security check needs the shopper; the scroll padding follows it.
      if (window.ResizeObserver) new ResizeObserver(() => { if (root === dialog) paintFooter(); }).observe(el('footer'));
      all('[data-rossi-room-product]').forEach(node => { node.textContent = data.title || ''; });
      el('privacy').textContent = data.privacyNote || '';
    }
    // Under <body> no transformed or filtered ancestor can break position: fixed.
    if (root.parentNode !== document.body) document.body.append(root);
    opener = trigger;
    const thumb = el('thumb');
    setSrc(thumb, thumbUrl(product.url));
    thumb.hidden = !thumb.getAttribute('src');
    // A kept result belongs to the variant it was made for (R28).
    if (state === 'result' && resultVariant !== variantId()) {
      state = 'place';
      showError('');
    }
    root.hidden = false;
    document.documentElement.classList.add('rossi-room-open');
    holdBack();
    loadBotCheck();
    checkHealth();
    // Signed out it only opens from "#room-preview" (QR handoff, return from sign-in) or in the theme
    // editor, and asks to sign in.
    if (state === 'start') refreshAccount();
    paint();
    enter();
  }

  // A generation in flight keeps running; reopening shows its progress or its result.
  function close() {
    if (!isOpen()) return;
    closeViewer();
    root.hidden = true;
    document.documentElement.classList.remove('rossi-room-open');
    paintCard();
    // Drop the entry open() added; popstate below ignores this one. Never a second back() while one
    // is still on its way: that one would leave the product page.
    if (!closing && history.state && history.state.rossiRoom) {
      closing = true;
      history.back();
    }
    if (opener && opener.isConnected) opener.focus({ preventScroll: true });
  }

  // Android's back gesture (and iOS swipe-back) closes the dialog instead of leaving the product
  // page, which would throw away a preview that is already counted. Same URL; on product pages the
  // theme only ever replaceState()s, so the entry never fights it.
  function holdBack() {
    if (!(history.state && history.state.rossiRoom)) history.pushState(Object.assign({}, history.state, { rossiRoom: true }), '');
  }

  function disclose(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    const expand = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expand));
    panel.hidden = !expand;
    const input = expand && panel.querySelector('input');
    if (input) input.focus();
  }

  function placePin(event) {
    const box = el('photo').getBoundingClientRect();
    // detail is 0 for Enter/Space on the button: that puts the pin in the centre.
    pin = event.detail && box.width
      ? [clamp(Math.round((event.clientX - box.left) / box.width * 100)), clamp(Math.round((event.clientY - box.top) / box.height * 100))]
      : [50, 50];
    paintPin();
    announce(el('spot-state').textContent);
  }

  function movePin(event, move) {
    event.preventDefault();
    const [x, y] = pin || [50, 50];
    pin = [clamp(x + move[0]), clamp(y + move[1])];
    paintPin();
    announce(el('spot-state').textContent);
  }

  const actions = {
    close,
    backdrop: () => { if (state !== 'generating') close(); },
    generate,
    cancel,
    save: () => save(isTouch()),
    download: () => save(false),
    cart: addToCart,
    change: () => setRoom(null),
    again: () => setState('place'),
    reuse: () => { if (last) setRoom(last.blob, last.size); },
    forget: forgetLastPhoto,
    signin: signIn,
    spot: placePin,
    unpin: () => {
      pin = null;
      paintPin();
      el('spot').focus();
      announce(el('spot-state').textContent);
    },
    disclose: (event, button) => disclose(button),
    original: () => { showing = 'original'; paintResult(); },
    preview: () => { showing = 'preview'; paintResult(); },
    zoom: openViewer,
    'viewer-zoom': () => zoom(),
    'viewer-close': closeViewer,
  };

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target && target.closest('[data-rossi-room-open]');
    // The theme editor previews without a customer; there the merchant still gets the dialog (the
    // Worker refuses any preview without a pass anyway).
    if (trigger) return signedIn() || (window.Shopify && window.Shopify.designMode) ? open(trigger) : signIn();
    if (!isOpen() || !root.contains(target)) return;
    const modeButton = target.closest('[data-rossi-room-mode]');
    if (modeButton) {
      mode = modeButton.dataset.rossiRoomMode;
      return paint();
    }
    const button = target.closest('[data-rossi-room-action]');
    const action = button && button.dataset.rossiRoomAction;
    // hasOwnProperty, not Object.hasOwn (ES2022): on older iOS a throw here would leave every
    // button in the open dialog dead, ✕ included.
    if (Object.prototype.hasOwnProperty.call(actions, action)) actions[action](event, button);
  });

  document.addEventListener('dblclick', event => {
    if (isOpen() && event.target === el('viewer-img')) zoom(undefined, event);
  });

  // Capture phase: the theme reports a variant switch with a non-bubbling change on the product form
  // (theme.js optionChange, after select[name="id"] is updated), and the card must not keep offering
  // a preview made for another variant.
  document.addEventListener('change', event => {
    if (!isOpen()) return paintCard();
    if (!root.contains(event.target)) return;
    const input = event.target;
    if (input.classList.contains('rossi-room__file')) {
      const file = input.files[0];
      input.value = '';   // so choosing the same file again still fires change
      // A picker opened before a notice replaced the controls still hands its file over.
      if (state === 'start' && !notice()) useFile(file);
    } else if (input === el('consent')) {
      paintGenerate();
    }
  }, true);

  // Handled on the whole dialog so a drop that misses the zone cannot navigate away from the product page.
  document.addEventListener('dragover', event => {
    if (!isOpen() || !root.contains(event.target)) return;
    event.preventDefault();
    el('drop').classList.toggle('is-over', state === 'start');
  });
  document.addEventListener('dragleave', event => {
    if (isOpen() && !root.contains(event.relatedTarget)) el('drop').classList.remove('is-over');
  });
  document.addEventListener('drop', event => {
    if (!isOpen() || !root.contains(event.target)) return;
    event.preventDefault();
    el('drop').classList.remove('is-over');
    if (state === 'start' && !notice()) useFile(event.dataTransfer.files[0]);
  });

  // Another site dialog opened on top of this one (e.g. the vendor notice popup) keeps its own focus and keys.
  const inOtherDialog = node => node instanceof Element && !root.contains(node) && Boolean(node.closest('[aria-modal="true"]'));

  document.addEventListener('keydown', event => {
    if (!isOpen() || inOtherDialog(event.target)) return;
    const viewerOpen = !el('viewer').hidden;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (viewerOpen) closeViewer(); else close();
      return;
    }
    if (ARROWS[event.key] && event.target === el('spot')) return movePin(event, ARROWS[event.key]);
    if (event.key !== 'Tab') return;
    const scope = viewerOpen ? el('viewer') : el('panel');
    const items = Array.from(scope.querySelectorAll('button:not(:disabled), input:not(:disabled), iframe, [tabindex="0"]')).filter(item => item.getClientRects().length);
    if (!items.length) return;
    const first = items[0], end = items[items.length - 1], active = document.activeElement;
    // An interactive Turnstile check renders its iframe in a closed shadow root that
    // querySelectorAll cannot see. When it is the next stop after the last control, Tab is left to
    // the browser; tabbing out of it again is caught by the focusin handler (R24).
    const check = el('turnstile');
    const intoCheck = !viewerOpen && check.classList.contains('is-interactive') && Boolean(end.compareDocumentPosition(check) & Node.DOCUMENT_POSITION_FOLLOWING);
    if (event.shiftKey && (active === first || active === scope || !scope.contains(active))) { event.preventDefault(); end.focus(); }
    else if (!event.shiftKey && ((active === end && !intoCheck) || !scope.contains(active))) { event.preventDefault(); first.focus(); }
  }, true);

  // Catches focus that leaves another way (e.g. tabbing out of the Turnstile iframe).
  document.addEventListener('focusin', event => {
    if (!isOpen() || root.contains(event.target) || inOtherDialog(event.target)) return;
    (el('viewer').hidden ? el('panel') : el('viewer-close')).focus({ preventScroll: true });
  });

  // Back gesture: closes the viewer first, then the dialog (see holdBack).
  window.addEventListener('popstate', () => {
    if (closing) {
      closing = false;
      if (isOpen()) holdBack();   // reopened before that back() landed
      return;
    }
    if (!isOpen()) return;
    if (el('viewer').hidden) return close();
    closeViewer();
    holdBack();
  });

  document.addEventListener('visibilitychange', () => {
    if (request && document.visibilityState === 'visible') holdScreen();
  });

  // Configured badly or no usable product image: remove the card before anyone taps it.
  const boot = settings();
  if (!serviceUrl(boot.endpoint) || !reference(boot)) disable();

  // U7 promises 7 days: an expired photo goes on the next product page view, not only when step 1
  // is opened again. Looks only; never creates the database for shoppers who never used the feature.
  photos('readonly', store => store.get('last'), true).then(saved => { if (saved && !usable(saved)) forget(); });

  // A reload keeps history.state; an entry from the previous page instance is not ours to go back over.
  if (history.state && history.state.rossiRoom) history.replaceState(Object.assign({}, history.state, { rossiRoom: false }), '');

  // "#room-preview" (the desktop QR handoff, the return from sign-in) opens the dialog once, then the
  // hash is cleared. signIn()'s flag does the same in case the sign-in dropped the hash.
  let resume = location.hash === '#room-preview';
  if (resume) history.replaceState(history.state, '', location.pathname + location.search);
  try {
    if (sessionStorage.getItem('rossiRoomResume') === location.pathname) resume = true;
    sessionStorage.removeItem('rossiRoomResume');
  } catch (_) { /* storage blocked */ }
  const card = resume && document.querySelector('[data-rossi-room-open]');
  if (card) open(card);
})();
